<template>
  <TabbedBox>
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-processed-pricing">
        <i class="far fa-money-check-edit-alt"></i> Processed Pricing Groups
      </Tab>
      <Tab tabid="tab-service-pricing">
        <i class="far fa-tools"></i> Service Pricing Ranges
      </Tab>
      <Tab tabid="tab-sales-rates">
        <i class="far fa-cash-register"></i> Sales Rates
      </Tab>
      <Tab tabid="tab-price-list" v-if="isPriceListsAvailable">
        <i class="far fa-list-alt"></i> Price List
      </Tab>
      <Tab tabid="tab-price-settings">
        <i class="far fa-cogs"></i> Pricing Settings
      </Tab>
      <Tab tabid="tab-activity-log">
        <i class="far fa-history"></i> Activity Log
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-processed-pricing">
        <div class="row">
          <div class="col-lg-12">
            <div v-if="(userHasCreatePermission(PermissionTypes.PricingPermissionTypes.pricingConfig) || userHasUpdatePermission(PermissionTypes.PricingPermissionTypes.pricingConfig)) && !stockPricingGroupEditing" style="clear: both;">
              <ActionDropMenu class="float-right">
                <DropdownMenuItem @click="newStockPricingGroup" v-if="userHasCreatePermission(PermissionTypes.PricingPermissionTypes.pricingConfig)" icon="fa-plus">New Pricing Group </DropdownMenuItem>
                <DropdownMenuItem @click="stockPricingGroupEditing=true" v-if="userHasUpdatePermission(PermissionTypes.PricingPermissionTypes.pricingConfig)" icon="fa-sort-amount-up-alt">Edit Pricing Group Order </DropdownMenuItem>
              </ActionDropMenu>
            </div>
            <h3>Processed Pricing Group List</h3>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-12">
            <StaticDataTable class="static-table-list" :editing="stockPricingGroupEditing" :key="stockPricingGroupKey" @sortUpdated="stockPriceGroupOrder = $event" >
              <template v-slot:header>
                <tr>
                  <th v-if="stockPricingGroupEditing"></th>
                  <th width="1%"></th>
                  <th>Name</th>
                  <th class="text-center">Active</th>
                  <th class="text-center">Stock Linked</th>
                  <th><Tooltip label="Minimum Size applied to pricing" helpStyle>Min Size Charge</Tooltip></th>
                  <th><Tooltip label="Minimum Price applied to each individual item in the group" helpStyle>Min Price Per Item</Tooltip></th>
                  <th><Tooltip label="Minimum Price applied to each group"  helpStyle>Min Price Per Group</Tooltip></th>
                  <th><Tooltip label="Minimum amount that will be charged for an order/invoice containing this price group"  helpStyle>Min Invoice Charge</Tooltip></th>
                  <th>Production Code</th>
                  <th>Actions</th>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="priceGroup in stockPriceGroupList" :key="priceGroup.id" :data-sort-id="priceGroup.id" class="static-table-sortable">
                  <td v-if="stockPricingGroupEditing" width="16px"><div class="static-datatable-row-draggable"><i class="fal fa-bars"></i> </div></td>
                  <td><i class="fas fa-star default-list-item" v-if="priceGroup.isDefault"></i></td>
                  <td class="font-weight-bold">{{priceGroup.name}}</td>
                  <td class="text-center" v-html="dataDisplayTableBoolean(priceGroup.isActive, { showFalse: true })"></td>
                  <td class="text-center" v-html="dataDisplayTableBoolean(priceGroup.isStockLinked)"></td>
                  <td v-html="dataDisplayTableArea(priceGroup.minChargeQuantity)"></td>
                  <td>{{priceGroup.minChargeItemPrice | formatCurrency}}</td>
                  <td>{{priceGroup.minChargePrice | formatCurrency}}</td>
                  <td>{{priceGroup.minChargeInvoice | formatCurrency}}</td>
                  <td>{{priceGroup.productionCode}}</td>
                  <td>
                    <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.PricingPermissionTypes.pricingConfig)" :tableAction="true">
                      <DropdownMenuItem @click="setStockPricingGroupDefault(priceGroup.id)" :disabled="priceGroup.isDefault" icon="fa-star">Make Default</DropdownMenuItem>
                      <DropdownMenuDivider />
                      <DropdownMenuItem @click="editStockPricingGroup(priceGroup)" icon="fa-pencil">Edit Group</DropdownMenuItem>
                      <DropdownMenuItem @click="setStockPricingGroupActive(priceGroup.id, false)" icon="fa-times" v-if="priceGroup.isActive">Make Inactive</DropdownMenuItem>
                      <DropdownMenuItem @click="setStockPricingGroupActive(priceGroup.id, true)" icon="fa-check" v-if="!priceGroup.isActive">Make Active</DropdownMenuItem>
                    </ActionDropMenu>
                  </td>
                </tr>
              </template>
            </StaticDataTable>

            <FormGroup>
              <div v-if="stockPricingGroupEditing">
                <FormHorizontalRule />
                <FormSubmit label="Update" :submitting="stockPriceGroupSubmitting" @cancelled="cancelStockPricingGroupEditing" @submitted="submitStockPricingGroupEditing"></FormSubmit>
              </div>
            </FormGroup>
          </div>
        </div>
      </TabPage>
      <TabPage tabid="tab-service-pricing">
        <div class="row">
          <div class="col-lg-12">
            <div v-if="userHasCreatePermission(PermissionTypes.PricingPermissionTypes.pricingConfig)" style="clear: both;">
              <ActionDropMenu class="float-right">
                <DropdownMenuItem @click="newServicePriceRange" icon="fa-plus">New Price Range</DropdownMenuItem>
              </ActionDropMenu>
            </div>
            <h3>Service Pricing Range List</h3>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-12">
            <StaticDataTable class="static-table-list">
              <template v-slot:header>
                <tr>
                  <th>Name</th>
                  <th class="text-center">Active</th>
                  <th>Min</th>
                  <th>Max</th>
                  <th>Price Groups</th>
                  <th>Actions</th>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="priceRange in servicePriceRangeList" :key="priceRange.id">
                  <td class="font-weight-bold">{{priceRange.name}}</td>
                  <td class="text-center" v-html="dataDisplayTableBoolean(priceRange.isActive, { showFalse: true })"></td>
                  <td>{{priceRange.minRange}}mm</td>
                  <td>{{priceRange.maxRange}}mm</td>
                  <td>{{priceRangePriceGroupList(priceRange)}}</td>
                  <td>
                    <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.PricingPermissionTypes.pricingConfig)" :tableAction="true">
                      <DropdownMenuItem @click="editServicePriceRange(priceRange)" icon="fa-pencil">Edit Price Range</DropdownMenuItem>
                      <DropdownMenuItem @click="setServicePriceRangeActive(priceRange.id, false)" icon="fa-times" v-if="priceRange.isActive">Make Inactive</DropdownMenuItem>
                      <DropdownMenuItem @click="setServicePriceRangeActive(priceRange.id, true)" icon="fa-check" v-if="!priceRange.isActive">Make Active</DropdownMenuItem>
                    </ActionDropMenu>
                  </td>
                </tr>
              </template>
            </StaticDataTable>
          </div>
        </div>
      </TabPage>

      <TabPage tabid="tab-sales-rates">
        <div class="row">
          <div class="col-lg-12">
            <div v-if="userHasCreatePermission(PermissionTypes.PricingPermissionTypes.pricingConfig)" style="clear: both;">
              <ActionDropMenu class="float-right">
                <DropdownMenuItem @click="newSalesRate" icon="fa-plus">New Sales Rate</DropdownMenuItem>
              </ActionDropMenu>
            </div>
            <h3>Sales Rate List</h3>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-12">
            <StaticDataTable class="static-table-list">
              <template v-slot:header>
                <tr>
                  <th width="1%"></th>
                  <th>Name</th>
                  <th>Code</th>
                  <th class="text-center">Active</th>
                  <th>Percentage</th>
                  <th>Actions</th>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="salesRate in salesRateList" :key="salesRate.id">
                  <td><i class="fas fa-star default-list-item" v-if="salesRate.isDefault"></i></td>
                  <td class="font-weight-bold">{{salesRate.name}}</td>
                  <td class="font-weight-bold">{{salesRate.code}}</td>
                  <td class="text-center" v-html="dataDisplayTableBoolean(salesRate.isActive, { showFalse: true })"></td>
                  <td>{{salesRate.percentAdjust | formatPercent}}</td>
                  <td>
                    <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.PricingPermissionTypes.pricingConfig)" :tableAction="true">
                      <DropdownMenuItem @click="setSalesRateDefault(salesRate.id)" :disabled="salesRate.isDefault" icon="fa-star">Make Default</DropdownMenuItem>
                      <DropdownMenuDivider />
                      <DropdownMenuItem @click="editSalesRate(salesRate)" icon="fa-pencil">Edit Sales Rate</DropdownMenuItem>
                      <DropdownMenuItem @click="setSalesRateActive(salesRate.id, false)" icon="fa-times" v-if="salesRate.isActive">Make Inactive</DropdownMenuItem>
                      <DropdownMenuItem @click="setSalesRateActive(salesRate.id, true)" icon="fa-check" v-if="!salesRate.isActive">Make Active</DropdownMenuItem>
                    </ActionDropMenu>
                  </td>
                </tr>
              </template>
            </StaticDataTable>
          </div>
        </div>
      </TabPage>

      <TabPage tabid="tab-price-list" v-if="isPriceListsAvailable">
        <div class="row">
          <div class="col-lg-12">
            <div v-if="userHasUpdatePermission(PermissionTypes.PricingPermissionTypes.pricingConfig) && !priceListConfig.editing" style="clear: both;">
              <ActionDropMenu class="float-right">
                <DropdownMenuItem @click="priceListConfig.editing=true" icon="fa-pencil">Edit Price List Config</DropdownMenuItem>
              </ActionDropMenu>
            </div>
            <h3>Global Price List Configuration</h3>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-12">
            <FormGroup :form="priceListConfig.editorForm">
              
              <PriceListConfigEditor 
                :config="priceListConfig.configData"
                :stockPriceGroupList="stockPriceGroupList"
                :editing="priceListConfig.editing"
                @update="priceListConfig.configData = $event"
                @updateOptions="priceListConfig.configData.reportOptions = $event"
              />

              <div v-if="priceListConfig.editing">
                <FormHorizontalRule />
                <FormSubmit label="Update" :submitting="priceListConfig.submitting" @cancelled="cancelPriceListConfigEditing" @submitted="submitPriceListConfigEditing"></FormSubmit>
              </div>
            </FormGroup>
          </div>
        </div>
      </TabPage>

      <TabPage tabid="tab-price-settings">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.PricingPermissionTypes.pricingConfig) && !pricingSettings.editing" class="float-right">
              <DropdownMenuItem @click="pricingSettings.editing = true" icon="fa-pencil">Edit Pricing Settings</DropdownMenuItem>
            </ActionDropMenu>
            <ContentTitle>Pricing Settings</ContentTitle>
            <ContentSubTitle>General settings for the Pricing module</ContentSubTitle>
          </div>
        </div>
        <FormGroup :labelColumnSize="6" :form="pricingSettings.form">
          <FormAccountSetting label="Pricing System" helpDefault="Complex">
            <template v-slot:helpContent>
              <p>The pricing system used for the account. Options are:</p>
              <p><strong>Simple:</strong> Customer price lists are not available, only default sales rates per customer is used for pricing.<br />
              With the simple pricing system, new contacts can be created via order entry.</p>
              <p><strong>Complex:</strong> Customer price lists are available. New customers will require a price list created when creating the customers.<br />
              New contacts cannot be created via order entry, they must be created prior to order entry.</p>
            </template>
            <template v-slot:entry>
              <div v-if="!pricingSettings.editing">{{getConfigSetting(AccountSettingKeys.pricingSystemType) | formatLookupName(PricingSystemTypeLookup)}}</div>
              <FormSelect v-if="pricingSettings.editing" :options="PricingSystemTypeLookup" :value="getConfigSetting(AccountSettingKeys.pricingSystemType)" @input="setConfigSetting(AccountSettingKeys.pricingSystemType, $event)" />
            </template>
          </FormAccountSetting>
          
          <FormSubmit v-if="pricingSettings.editing" label="Update" :submitting="pricingSettings.submitting" @submitted="submitPricingConfigSettings" @cancelled="cancelPricingConfigSettings" />
        </FormGroup>
      </TabPage>

      <TabPage tabid="tab-activity-log">
        <div class="row">
          <div class="col-lg-12">
            <ActivityLogStream ref="activityStream" url="/pricing/config/activitystream">
              <template v-slot:title><h3>Recent Activity</h3></template>
            </ActivityLogStream>
          </div>
        </div>
      </TabPage>
    </template>
  </TabbedBox>
</template>

<script>
import _ from 'lodash'
import { mapState } from "vuex"

import form from '@/mixins/form'
import TabbedBox from "@/components/layout/tabbedBox.vue"
import Tab from "@/components/layout/tab.vue"
import TabPage from "@/components/layout/tabPage.vue"
import StaticDataTable from "@/components/table/staticDataTable.vue"
import DataDisplayMixin from '@/mixins/dataDisplay'
import StockFormatterMixin from '@/modules/inventory/mixins/stockFormatter.js'

import StockPriceGroupDialog from '@/modules/pricing/views/stockPriceGroupDialog.vue'
import SalesRateDialog from '@/modules/pricing/views/salesRateDialog.vue'
import ServicePriceRangeDialog from '@/modules/pricing/views/servicePriceRangeDialog.vue'
import PriceListConfigEditor from '@/modules/pricing/components/priceListConfigEditor.vue'

import { AccountSettingKeys, PricingSystemTypes, PricingSystemTypeLookup } from '@/store/types'

export default {
  name: "PricingConfiguration",
  mixins: [form, DataDisplayMixin, StockFormatterMixin],
  components: {
    TabbedBox,
    Tab,
    TabPage,
    StaticDataTable,
    PriceListConfigEditor
  },
  data: () => {
    return {
      salesRateList: [],
      servicePriceRangeList: [],

      nestedCategoryList: [],
      stockCategoryEditing: false,
      stockCategorySubmitting: false,

      stockPricingGroupEditing: false,
      stockPriceGroupSubmitting: false,
      stockPricingGroupKey: 0,
      stockPriceGroupList: [],
      stockPriceGroupOrder: [],

      stockContainerEditing: false,
      stockContainerSubmitting: false,
      stockContainerOrder: [],
      stockContainerKey: 0,
      stockContainerList: [],

      priceListConfig: {
        editorForm: {},
        editing: false,
        configData: {},
        submitting: false
      },

      pricingSettings: {
        editing: false,
        submitting: false,
        form: {},
        settings: {}
      }      
    }
  },
  created() {
    this.AccountSettingKeys = AccountSettingKeys
    this.PricingSystemTypeLookup = PricingSystemTypeLookup
  },
  mounted() {
    this.requestData()
  },
  computed: {
    ...mapState({
      accountPricingSystemType: state => state.account.pricingSystemType
    }),
    isPriceListsAvailable() {
      return this.accountPricingSystemType != PricingSystemTypes.simple
    }
  },
  methods: {
    reloadData() {
      this.$refs.activityStream.reloadData()
      this.requestData()
    },
    requestData() {
      this.$apiService.pricingService.getStockPriceGroupList().then(response => {
        this.$data.stockPriceGroupList = response.data
        this.$data.stockPriceGroupOrder = this.$data.stockPriceGroupList.map(group => group.id)
      })
      this.$apiService.pricingService.getSalesRateList().then(response => {
        this.$data.salesRateList = response.data
      })
      this.$apiService.pricingService.getServicePriceRangeList().then(response => {
        this.$data.servicePriceRangeList = response.data
      })
      this.$apiService.pricingService.getGlobalPriceListConfig().then(response => {
        this.$data.priceListConfig.configData = response.data
      })

      this.$apiService.pricingService.getConfigSettings().then(response => {
        this.$data.pricingSettings.settings = response.data
      })
    },

    // Stock Pricing
    newStockPricingGroup() {
      this.displayModal(StockPriceGroupDialog, { 
        isCreateMode: true
      })
      .then((success) => {
        if(success) {
          this.successToast('Stock Pricing Group Added')
          this.reloadData()
        }
      })
    },
    editStockPricingGroup(stockPriceGroup) {
      this.displayModal(StockPriceGroupDialog, { 
        isCreateMode: false
      }, stockPriceGroup)
      .then((success) => {
        if(success) {
          this.successToast('Stock Pricing Group Updated')
          this.reloadData()
        }
      })
    },
    setStockPricingGroupDefault(stockPriceGroupId) {
      this.$apiService.pricingService.updateStockPriceGroup(stockPriceGroupId, { isDefault: true })
      .then(response => {
        this.successToast('Stock Pricing Group Updated')
        this.reloadData()
      })
    },
    setStockPricingGroupActive(stockPriceGroupId, isActive) {
      this.$apiService.pricingService.updateStockPriceGroup(stockPriceGroupId, { isActive: isActive })
      .then(response => {
        this.successToast('Stock Pricing Group Updated')
        this.reloadData()
      })
    },
    cancelStockPricingGroupEditing() {
      this.stockPricingGroupEditing = false

      // Refresh the stock price group list
      this.$apiService.pricingService.getStockPriceGroupList().then(response => {
        this.$data.stockPriceGroupList = response.data

        this.$nextTick(() => {
          // JB: This is hacky, but cannot seem to get the table data to reset after sortable()
          this.$data.stockPricingGroupKey++
        })
      })
    },
    submitStockPricingGroupEditing() {
      this.$data.stockPriceGroupSubmitting = true

      var stockPriceGroupBatchData = this.stockPriceGroupOrder.map((groupId, index) => {
        return {
          id: groupId,
          sortOrder: index
        }
      })

      this.$apiService.pricingService.updateStockPriceGroupBatch({ 'groups': stockPriceGroupBatchData })
      .then(response => {
        this.stockPricingGroupEditing = false
      })
      .catch(err => {
        this.displayNetworkError(err, 'Failed to update', 'Error while attempting to update stock price groups')                
      })
      .finally(() => {
        this.$data.stockPriceGroupSubmitting = false
      })
    },

    // Glass Price Group
    newSalesRate() {
      this.displayModal(SalesRateDialog, { 
        isCreateMode: true
      })
      .then((success) => {
        if(success) {
          this.successToast('Sales Rate Added')
          this.reloadData()
        }
      })
    },
    editSalesRate(salesRate) {
      this.displayModal(SalesRateDialog, { 
        isCreateMode: false
      }, salesRate)
      .then((success) => {
        if(success) {
          this.successToast('Sales Rate Updated')
          this.reloadData()
        }
      })
    },    
    setSalesRateActive(salesRateId, isActive) {
      this.$apiService.pricingService.updateSalesRate(salesRateId, { isActive: isActive })
      .then(response => {
        this.successToast('Sales Rate Updated')
        this.reloadData()
      })
    },
    setSalesRateDefault(salesRateId) {
      this.$apiService.pricingService.updateSalesRate(salesRateId, { isDefault: true })
      .then(response => {
        this.successToast('Sales Rate Updated')
        this.reloadData()
      })
    },

    // Service Price Range
    newServicePriceRange() {
      this.displayModal(ServicePriceRangeDialog, { 
        isCreateMode: true
      })
      .then((success) => {
        if(success) {
          this.successToast('Service Price Range Added')
          this.reloadData()
        }
      })
    },
    editServicePriceRange(servicePriceRange) {
      this.displayModal(ServicePriceRangeDialog, { 
        isCreateMode: false
      }, servicePriceRange)
      .then((success) => {
        if(success) {
          this.successToast('Service Price Range Updated')
          this.reloadData()
        }
      })
    },    
    setServicePriceRangeActive(servicePriceRangeId, isActive) {
      this.$apiService.pricingService.updateServicePriceRange(servicePriceRangeId, { isActive: isActive })
      .then(response => {
        this.successToast('Service Price Range Updated')
        this.reloadData()
      })
    },
    priceRangePriceGroupList(servicePriceRange) {

      return _.compact(servicePriceRange.stockPriceGroups.map(priceGroup => {
        return priceGroup.name
      })).join(', ')
    },

    cancelPriceListConfigEditing() {
      this.$data.priceListConfig.editing = false

      // Reload data back to the current
      this.$apiService.pricingService.getGlobalPriceListConfig().then(response => {
        this.$data.priceListConfig.configData = response.data
      })
    },
    submitPriceListConfigEditing() {
      this.$data.priceListConfig.submitting = true

      var updateData = {
        configuration: this.$data.priceListConfig.configData.configList,
        priceGroupDisplay: _.get(this.$data.priceListConfig, 'configData.reportOptions.priceGroupDisplay.display', ''),
        deliveryInfoDisplay: _.get(this.$data.priceListConfig, 'configData.reportOptions.deliveryInfoDisplay.display', ''),
        reportNotes: _.get(this.$data.priceListConfig, 'configData.reportOptions.reportNotes.display', ''),
        headingText: _.get(this.$data.priceListConfig, 'configData.reportOptions.headingText.display', ''),
        footerText: _.get(this.$data.priceListConfig, 'configData.reportOptions.footerText.display', ''),
        noteListTitle: _.get(this.$data.priceListConfig, 'configData.reportOptions.noteListTitle.display', '')
      }
      
      // Send data to server
      this.$apiService.pricingService.updateGlobalPriceListConfig(updateData).then(response => {
        this.$data.priceListConfig.configData = response.data
        this.$data.priceListConfig.editing = false
      })
      .catch(err => {
        // TODO: Display error somewhere
      })
      .finally(() => {
        this.$data.priceListConfig.submitting = false
      })
    },

    // General Config Settings
    getConfigSetting(settingKey, defaultValue = '') {
      if(this.pricingSettings.settings[settingKey] !== undefined)
        return this.pricingSettings.settings[settingKey]
      return defaultValue
    },
    getConfigSettingBoolean(settingKey, defaultValue = false) {
      if(this.pricingSettings.settings[settingKey] !== undefined)
        return this.parseBoolean(this.pricingSettings.settings[settingKey])
      return defaultValue
    },
    getConfigSettingInt(settingKey, defaultValue = 0) {
      if(this.pricingSettings.settings[settingKey] !== undefined)
        return parseInt(this.pricingSettings.settings[settingKey])
      return defaultValue
    },
    getConfigSettingNumber(settingKey, defaultValue = 0) {
      if(this.pricingSettings.settings[settingKey] !== undefined)
        return this.parseDecimal(this.pricingSettings.settings[settingKey])
      return defaultValue
    },
    setConfigSetting(settingKey, value) {
      this.pricingSettings.settings[settingKey] = value
    },

    submitPricingConfigSettings() {
      this.$data.pricingSettings.submitting = true
      this.$apiService.pricingService.updateConfigSettings({ settings: this.$data.pricingSettings.settings })
      .then(response => {
        this.$data.pricingSettings.editing = false
        this.successToast('Pricing Config Settings updated')
      })
      .catch(err => {
        this.$data.pricingSettings.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.pricingSettings.submitting = false
      })
    },
    cancelPricingConfigSettings() {
      this.$data.pricingSettings.editing = false
      this.$apiService.pricingService.getConfigSettings()
      .then(response => {
        this.$data.pricingSettings.settings = response.data
      })
    },
  }
}

</script>